import React, {useState, useCallback} from 'react';
import {
  ReactFlowProvider,
  addEdge,
  Background,
  Controls,
  MiniMap,
  useNodesState,
  useEdgesState,
  ReactFlow,
  Handle,
  Position,
} from '@xyflow/react';
import '@xyflow/react/dist/style.css';
import {Resizable} from 're-resizable';

// Initial Swimlanes
const initialSwimlanes = [
  {id: 'lane-1', label: 'Lane 1', y: 0, height: 150},
  {id: 'lane-2', label: 'Lane 2', y: 150, height: 150},
];

// Initial Nodes and Edges
const initialNodes = [
  {id: '1', position: {x: 100, y: 50}, data: {label: 'Event 1'}, type: 'customNode'},
  {id: '2', position: {x: 300, y: 50}, data: {label: 'Event 2'}, type: 'customNode'},
  {id: '3', position: {x: 100, y: 200}, data: {label: 'Event 3'}, type: 'customNode'},
  {id: '4', position: {x: 300, y: 200}, data: {label: 'Event 4'}, type: 'customNode'},
];

const initialEdges = [
  {id: 'e1-2', source: '1', target: '2', type: 'smoothstep'},
  {id: 'e3-4', source: '3', target: '4', type: 'smoothstep'},
];

// Initial Timeline Markers
const initialTimelineMarkers = [
  {id: 'marker-1', label: 'Marker 1', x: 100, width: 200},
  {id: 'marker-2', label: 'Marker 2', x: 300, width: 200},
];

// Custom Node Component
const CustomNode = ({data}) => (
  <div
    style={{
      padding: '10px',
      backgroundColor: '#fff',
      border: '1px solid #ddd',
      borderRadius: '5px',
      textAlign: 'center',
    }}>
    {data.label}
    <Handle type="target" position={Position.Left} style={{background: '#555'}} />
    <Handle type="source" position={Position.Right} style={{background: '#555'}} />
  </div>
);

// Node Types
const nodeTypes = {
  customNode: CustomNode,
};

// Main Workflow Component
const SwimlaneTimelineWorkflow = () => {
  const [swimlanes, setSwimlanes] = useState(initialSwimlanes);
  const [nodes, setNodes, onNodesChange] = useNodesState(initialNodes);
  const [edges, setEdges, onEdgesChange] = useEdgesState(initialEdges);
  const [transform, setTransform] = useState({x: 0, y: 0, zoom: 1});
  const [timelineMarkers, setTimelineMarkers] = useState(initialTimelineMarkers);

  // Add a new swimlane dynamically
  const addSwimlane = useCallback(() => {
    const newLaneId = `lane-${swimlanes.length + 1}`;
    const newLaneY = swimlanes.reduce((acc, lane) => acc + lane.height, 0);

    setSwimlanes(lanes => [
      ...lanes,
      {id: newLaneId, label: `Lane ${swimlanes.length + 1}`, y: newLaneY, height: 150},
    ]);
  }, [swimlanes]);

  // Add a new timeline marker dynamically
  const addTimelineMarker = useCallback(() => {
    const newMarkerId = `marker-${timelineMarkers.length + 1}`;
    const newMarkerX = timelineMarkers.reduce((acc, marker) => acc + marker.width, 0) + 100;

    setTimelineMarkers(markers => [
      ...markers,
      {id: newMarkerId, label: `Marker ${timelineMarkers.length + 1}`, x: newMarkerX, width: 2},
    ]);
  }, [timelineMarkers]);

  // Handle edge creation (drag-and-connect)
  const onConnect = useCallback(params => setEdges(eds => addEdge(params, eds)), [setEdges]);
  // Handle workflow movement and zoom
  const onMove = useCallback(
    (event, viewport) => {
      setTransform(viewport);
    },
    [setTransform],
  );

  // Save swimlane positions and heights before resizing
  const [swimlaneResizing, setSwimlaneResizing] = useState<any>(undefined);

  // Save timeline marker positions and widths before resizing
  const [markerResizing, setMarkerResizing] = useState<any>(undefined);

  return (
    <div style={{position: 'relative', height: '90vh', width: '100%'}}>
      {/* Render Swimlanes */}
      {swimlanes.map((lane, index) => (
        <Resizable
          className="flex items-center justify-center"
          size={{width: '100%', height: lane.height * transform.zoom}}
          enable={{top: false, right: false, bottom: true, left: false}}
          onResizeStart={(e, direction, ref) => {
            // save current position and height of swimlanes
            const sz = {};
            for (const l of swimlanes) {
              sz[l.id] = {y0: l.y, h0: l.height};
            }
            setSwimlaneResizing(sz);
            console.log('onResizeStart', sz);
          }}
          onResize={(e, direction, ref, d) => {
            if (!swimlaneResizing) return;
            console.log('onResize', e, direction, ref, d);
            const deltaH = d.height / transform.zoom;
            setSwimlanes(lanes =>
              lanes.map(l => {
                if (l.id === lane.id) {
                  return {...l, height: swimlaneResizing[l.id].h0 + deltaH};
                } else if (l.y > lane.y) {
                  return {...l, y: swimlaneResizing[l.id].y0 + deltaH};
                }
                return l;
              }),
            );

            setNodes(nds =>
              nds.map(node => {
                const laneForNode = swimlanes.find(
                  l => node.position.y >= l.y && node.position.y < l.y + l.height,
                );
                if (laneForNode) {
                  const newLane = swimlanes.find(l => l.id === laneForNode.id);
                  if (newLane) {
                    return {
                      ...node,
                      position: {
                        ...node.position,
                        y: newLane.y + newLane.height / 2 - 25,
                      },
                    };
                  }
                }
                return node;
              }),
            );
          }}
          handleStyles={{
            bottom: {
              pointerEvents: 'auto',
            },
          }}
          key={lane.id}
          style={{
            pointerEvents: 'none',
            position: 'absolute',
            top: transform.y + lane.y * transform.zoom,
            left: 0,
            backgroundColor: 'transparent',
            borderTop: index == 0 ? `${4 * transform.zoom}px dashed #ccc` : '',
            borderBottom: `${4 * transform.zoom}px dashed #ccc`,
            zIndex: 50,
          }}>
          <span
            style={{
              position: 'absolute',
              left: 10 * transform.zoom,
              top: 10 * transform.zoom,
              fontWeight: 'bold',
            }}>
            {lane.id}:{lane.label} ({lane.y}@{lane.height})
          </span>
        </Resizable>
      ))}

      {/* Render Timeline Markers */}
      {timelineMarkers.map((marker, index) => (
        <Resizable
          className="flex items-center justify-center"
          key={marker.id}
          size={{width: marker.width * transform.zoom, height: '100%'}}
          enable={{top: false, right: true, bottom: false, left: false}}
          onResizeStart={(e, direction, ref) => {
            // save current position and height of swimlanes
            const ms = {};
            for (const m of timelineMarkers) {
              ms[m.id] = {x0: m.x, w0: m.width};
            }
            setMarkerResizing(ms);
            console.log('onResizeStart', ms);
          }}
          onResize={(e, direction, ref, d) => {
            const deltaW = d.width / transform.zoom;
            setTimelineMarkers(markers =>
              markers.map(m => {
                if (m.id === marker.id) {
                  return {...m, width: markerResizing[m.id].w0 + deltaW};
                } else if (m.x > marker.x) {
                  return {...m, x: markerResizing[m.id].x0 + deltaW};
                }
                return m;
              }),
            );
          }}
          style={{
            pointerEvents: 'none',
            position: 'absolute',
            top: 0,
            left: transform.x + marker.x * transform.zoom,
            zIndex: 40,
            backgroundColor: 'transparent',
            borderLeft: index == 0 ? `${2 * transform.zoom}px dashed #ccc` : '',
            borderRight: `${2 * transform.zoom}px dashed #ccc`,
          }}
          handleStyles={{
            right: {
              pointerEvents: 'auto',
            },
          }}>
          <span
            style={{
              position: 'absolute',
              top: 10 * transform.zoom,
              left: 10 * transform.zoom,
              fontSize: 14 * transform.zoom,
              fontWeight: 'bold',
            }}>
            {marker.label}
          </span>
        </Resizable>
      ))}

      {/* React Flow Workflow */}
      <ReactFlow
        nodes={nodes}
        edges={edges}
        onEdgesChange={onEdgesChange}
        onConnect={onConnect}
        onMove={onMove}
        fitView
        nodeTypes={nodeTypes}
        style={{
          position: 'absolute',
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
        }}>
        <Background />
        <Controls />
        <MiniMap />
      </ReactFlow>

      {/* Add Swimlane Button */}
      <button
        onClick={addSwimlane}
        style={{
          position: 'absolute',
          top: 10,
          right: 10,
          zIndex: 10,
          padding: '10px 20px',
          backgroundColor: '#007bff',
          color: '#fff',
          border: 'none',
          borderRadius: '5px',
          cursor: 'pointer',
        }}>
        Add Swimlane
      </button>

      {/* Add Timeline Marker Button */}
      <button
        onClick={addTimelineMarker}
        style={{
          position: 'absolute',
          top: 50,
          right: 10,
          zIndex: 10,
          padding: '10px 20px',
          backgroundColor: '#28a745',
          color: '#fff',
          border: 'none',
          borderRadius: '5px',
          cursor: 'pointer',
        }}>
        Add Timeline Marker
      </button>
    </div>
  );
};

// Main App Component
const TestReactFlow = () => (
  <ReactFlowProvider>
    <SwimlaneTimelineWorkflow />
  </ReactFlowProvider>
);

export default TestReactFlow;
